'use client';

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

export type UserRole = 'admin' | 'staff';

export interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  name: string;
  initials: string;
}

export type Permission =
  | 'create_document'
  | 'edit_document' |'delete_document' |'view_document' |'manage_clients' |'manage_items' |'send_email' |'download_pdf' |'bulk_actions' |'view_dashboard';

// Both admin and staff have full permissions per user request
const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  admin: [
    'create_document', 'edit_document', 'delete_document', 'view_document',
    'manage_clients', 'manage_items', 'send_email', 'download_pdf',
    'bulk_actions', 'view_dashboard',
  ],
  staff: [
    'create_document', 'edit_document', 'delete_document', 'view_document',
    'manage_clients', 'manage_items', 'send_email', 'download_pdf',
    'bulk_actions', 'view_dashboard',
  ],
};

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (email: string, password: string, fullName: string, role?: UserRole) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  can: (action: Permission) => boolean;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  login: async () => ({ success: false }),
  signUp: async () => ({ success: false }),
  logout: async () => {},
  can: () => false,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();
  const router = useRouter();

  const buildAuthUser = useCallback(async (supabaseUser: any): Promise<AuthUser | null> => {
    if (!supabaseUser) return null;
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('full_name, role, initials')
        .eq('id', supabaseUser.id)
        .maybeSingle();

      const role: UserRole = (profile?.role as UserRole) || 'staff';
      const fullName = profile?.full_name || supabaseUser.email?.split('@')[0] || 'User';
      const initials = profile?.initials || fullName.slice(0, 2).toUpperCase();

      return {
        id: supabaseUser.id,
        email: supabaseUser.email || '',
        role,
        name: fullName,
        initials,
      };
    } catch {
      return {
        id: supabaseUser.id,
        email: supabaseUser.email || '',
        role: 'staff',
        name: supabaseUser.email?.split('@')[0] || 'User',
        initials: 'US',
      };
    }
  }, [supabase]);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const authUser = await buildAuthUser(session.user);
        setUser(authUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        const authUser = await buildAuthUser(session.user);
        setUser(authUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [buildAuthUser, supabase.auth]);

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { success: false, error: error.message };
      if (!data.user) return { success: false, error: 'No user returned from authentication.' };
      const authUser = await buildAuthUser(data.user);
      setUser(authUser);
      router.refresh();
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.message || 'An unexpected error occurred.' };
    }
  }, [supabase.auth, buildAuthUser, router]);

  const signUp = useCallback(async (
    email: string,
    password: string,
    fullName: string,
    role: UserRole = 'staff'
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const initials = fullName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName, role },
        },
      });

      if (error) return { success: false, error: error.message };
      if (!data.user) return { success: false, error: 'Sign-up failed. Please try again.' };

      // Insert profile row
      const { error: profileError } = await supabase.from('user_profiles').insert({
        id: data.user.id,
        email,
        full_name: fullName,
        role,
        initials,
      });

      if (profileError && profileError.code !== '23505') {
        // 23505 = unique violation (profile already exists), safe to ignore
        return { success: false, error: profileError.message };
      }

      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.message || 'An unexpected error occurred.' };
    }
  }, [supabase]);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    router.push('/sign-up-login-screen');
    router.refresh();
  }, [supabase.auth, router]);

  const can = useCallback((action: Permission): boolean => {
    if (!user) return false;
    return ROLE_PERMISSIONS[user.role].includes(action);
  }, [user]);

  const value = useMemo(() => ({ user, loading, login, signUp, logout, can }), [user, loading, login, signUp, logout, can]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
